import React, { useState, useEffect } from 'react';
import { useTasks } from '../../contexts/TaskContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  Card, CardContent, Grid, TextField, MenuItem, Button, Typography, Alert,
  Box, Chip, Paper, Tooltip, Container, CircularProgress
} from '@mui/material';
import {
  PlayArrow, Check, Error, Schedule, Refresh,
  Language, Translate, Add, VideoCameraBack, HelpOutline, Business
} from '@mui/icons-material';
import api from '../../services/api';
import { formatExecutiveSummary } from '../../utils/summaryFormatter';
import { formatDateTime } from '../../utils/dateFormatter';


// QualityLens Branding Theme
const THEME = {
  primary: '#0DA1B8',
  primaryDark: '#0C587D',
  primaryLight: '#3BC5D9',
  primaryUltraLight: '#F0FDFA',
  accent: '#00B4DB',
  accentLight: '#E0F2FE',
  accentUltraLight: '#F8FAFC',
  orange: '#0DA1B8',
  orangeLight: '#F0FDFA',
  background: '#FFFFFF',
  surface: '#F8FAFC',
  surfaceElevated: '#FFFFFF',
  border: '#E2E8F0',
  borderLight: '#F1F5F9',
  textPrimary: '#1E293B',
  textSecondary: '#64748B',
  textTertiary: '#94A3B8',
  success: '#10B981',
  successLight: '#F0FDF4',
  warning: '#F59E0B',
  warningLight: '#FFFBE8',
  error: '#EF4444',
  errorLight: '#FEF2F2',
  gradientPrimary: 'linear-gradient(135deg, #0083B0 0%, #00B4DB 100%)',
  gradientAccent: 'linear-gradient(135deg, #0DA1B8 0%, #0C587D 100%)',
  gradientSuccess: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
  shadowSm: '0 4px 20px rgba(0,0,0,0.03)',
  shadowMd: '0 8px 30px rgba(13, 161, 184, 0.08)',
  shadowLg: '0 12px 40px rgba(13, 161, 184, 0.12)',
};

const LANGS = [
  { code: "auto", name: "Auto Detect", icon: "🔍" },
  { code: "en", name: "English", icon: "🇺🇸" },
  { code: "as", name: "Assamese", icon: "🇮🇳" },
  { code: "bn", name: "Bengali", icon: "🇮🇳" },
  { code: "gu", name: "Gujarati", icon: "🇮🇳" },
  { code: "hi", name: "Hindi", icon: "🇮🇳" },
  { code: "kn", name: "Kannada", icon: "🇮🇳" },
  { code: "ml", name: "Malayalam", icon: "🇮🇳" },
  { code: "mr", name: "Marathi", icon: "🇮🇳" },
  { code: "ne", name: "Nepali", icon: "🇳🇵" },
  { code: "or", name: "Odia", icon: "🇮🇳" },
  { code: "pa", name: "Punjabi", icon: "🇮🇳" },
  { code: "sa", name: "Sanskrit", icon: "🇮🇳" },
  { code: "ta", name: "Tamil", icon: "🇮🇳" },
  { code: "te", name: "Telugu", icon: "🇮🇳" },
  { code: "ur", name: "Urdu", icon: "🇵🇰" },
];

const STATUS_CONFIG = {
  pending: {
    color: 'default',
    icon: <Schedule sx={{ color: THEME.textTertiary }} />,
    label: 'Pending',
    bgColor: THEME.surface,
    textColor: THEME.textTertiary
  },
  processing: {
    color: 'primary',
    icon: <Refresh sx={{ color: THEME.primary }} />,
    label: 'Processing',
    bgColor: THEME.primaryUltraLight,
    textColor: THEME.primary
  },
  completed: {
    color: 'success',
    icon: <Check sx={{ color: THEME.success }} />,
    label: 'Completed',
    bgColor: THEME.successLight,
    textColor: THEME.success
  },
  failed: {
    color: 'error',
    icon: <Error sx={{ color: THEME.error }} />,
    label: 'Failed',
    bgColor: THEME.errorLight,
    textColor: THEME.error
  }
};

export default function NewAnalysis() {
  const { tasks, addTask } = useTasks();
  const [url, setUrl] = useState('');
  const [lang, setLang] = useState('auto');
  const [target, setTarget] = useState('en');
  const [localLoading, setLocalLoading] = useState(false); // Only for submission request
  const [error, setError] = useState('');
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [progressPct, setProgressPct] = useState(0);
  const [resultData, setResultData] = useState(null);
  const { user, role } = useAuth();
  const [dealersList, setDealersList] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [quotaInfo, setQuotaInfo] = useState(null);

  // Super Admin: Fetch registered dealerships list
  useEffect(() => {
    if (role === 'super_admin') {
      api.get('/dashboard/super-admin/overview')
        .then(res => {
          const rawDealers = res.data?.dealers_data || [];
          const names = rawDealers.map(d => d.dealer_name || d.dealer_id).filter(Boolean);
          setDealersList(Array.from(new Set(names)));
        })
        .catch(err => console.warn('Could not fetch dealers list for super admin:', err));
    }
  }, [role]);

  // Fetch today's dealer quota
  useEffect(() => {
    const fetchQuota = async () => {
      try {
        const cached = localStorage.getItem('ql_dealer_quotas_cache');
        if (cached && user?.dealer_id) {
          const list = JSON.parse(cached);
          const found = list.find(d => d.dealer_id?.toLowerCase() === user.dealer_id?.toLowerCase());
          if (found) {
            setQuotaInfo(found);
            return;
          }
        }
      } catch {}

      try {
        const res = await api.get('/dealer/quota-status');
        if (res.data) setQuotaInfo(res.data);
      } catch (e) {}
    };
    fetchQuota();
  }, [user]);

  // Derived state: find the task in global context
  const currentTask = tasks.find(t => t.task_id === currentTaskId) || null;

  // Cleanup: if task completes/fails, we stop local "loading" if we were waiting (though we rely on context now)
  useEffect(() => {
    if (currentTask && (currentTask.status === 'completed' || currentTask.status === 'failed')) {
      setLocalLoading(false);
      if (currentTask.status === 'completed') {
        setProgressPct(100);
        // Fetch the full result inline
        if (currentTask.result_id) {
          api.get(`/results/${currentTask.result_id}`)
            .then(res => setResultData(res.data))
            .catch(err => console.warn('Could not fetch result inline:', err));
        }
      }
    }
  }, [currentTask]);

  // Simulate progressive percentage while processing
  useEffect(() => {
    if (!currentTask || currentTask.status !== 'processing') return;
    setProgressPct(prev => (prev < 1 ? 1 : prev)); // start at 1
    const interval = setInterval(() => {
      setProgressPct(prev => {
        if (prev >= 95) return prev; // slow down near end, wait for real completion
        // Increment faster early, slower later
        const increment = prev < 30 ? 3 : prev < 60 ? 2 : prev < 80 ? 1 : 0.5;
        return Math.min(95, prev + increment);
      });
    }, 1200);
    return () => clearInterval(interval);
  }, [currentTask?.status]);

  // Reset progress when new task starts
  useEffect(() => {
    if (currentTaskId) setProgressPct(0);
  }, [currentTaskId]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setCurrentTaskId(null);

    const cleanUrl = (url || '').trim();
    if (!cleanUrl) {
      setError('Please provide a valid CitNow video URL or token.');
      return;
    }

    // Pre-flight validation for CitNow authenticity
    const citnowDomainRegex = /citnow\.(com|co\.uk)/i;
    const isTokenOnly = /^[a-zA-Z0-9_-]{6,30}$/.test(cleanUrl);
    if (!citnowDomainRegex.test(cleanUrl) && !isTokenOnly) {
      setError('Invalid link rejected: CitNow video links must be hosted on citnow.com (e.g. https://southasia.citnow.com/vpwz-HTyJ9t). Non-CitNow URLs, placeholder values, and unsupported links are not accepted.');
      return;
    }

    setLocalLoading(true);

    try {
      const payload = {
        citnow_url: cleanUrl,
        transcription_language: lang,
        target_language: target
      };
      if (role === 'super_admin' && selectedDealer) {
        payload.dealer_id = selectedDealer;
      }

      const res = await api.post('/analyze', payload);

      const newTaskId = res.data.task_id;
      if (!newTaskId) {
        throw new Error('No task ID returned from server');
      }

      // Add to global context to start tracking
      addTask({
        task_id: newTaskId,
        status: 'pending',
        message: 'Queued for processing...',
        type: 'analysis',
        created_at: new Date().toISOString()
      });

      setCurrentTaskId(newTaskId);

    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Analysis failed to start');
      setLocalLoading(false);
    }
  };

  const resetForm = () => {
    setUrl('');
    setLang('auto');
    setTarget('en');
    setCurrentTaskId(null);
    setError('');
    setLocalLoading(false);
    setResultData(null);
    setProgressPct(0);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h2" // Larger, bolder font
          sx={{
            fontWeight: 800,
            letterSpacing: '-1.5px', // Tighter, elite feel
            color: THEME.textPrimary,
            mb: 2,
            background: THEME.gradientPrimary,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 4px 12px rgba(28,105,212,0.15))'
          }}
        >
          New Video Analysis
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: THEME.textSecondary,
            fontWeight: 400,
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6
          }}
        >
          Analyze QualityLens videos with AI-powered transcription, translation, and quality assessment
        </Typography>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={8}>
          <Card sx={{
            background: THEME.surfaceElevated,
            border: `1.5px solid ${THEME.border}`,
            borderRadius: 4,
            boxShadow: THEME.shadowLg,
            overflow: 'visible',
            position: 'relative'
          }}>
            <CardContent sx={{ p: 4 }}>
              {/* Daily Quota Pill & Exceeded Banner */}
              {quotaInfo && quotaInfo.daily_limit && (
                <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: THEME.textSecondary, fontWeight: 600 }}>
                    Dealership Quota: <strong>{user?.showroom_name || user?.dealer_id || 'Active Account'}</strong>
                  </Typography>
                  <Chip
                    size="small"
                    label={`Daily Quota: ${quotaInfo.used_today || 0} / ${quotaInfo.daily_limit} links today`}
                    sx={{
                      fontWeight: 700,
                      fontSize: '11px',
                      bgcolor: ((quotaInfo.used_today || 0) >= quotaInfo.daily_limit) ? '#FEF2F2' : '#F0FDF4',
                      color: ((quotaInfo.used_today || 0) >= quotaInfo.daily_limit) ? THEME.error : THEME.success,
                      border: `1px solid ${((quotaInfo.used_today || 0) >= quotaInfo.daily_limit) ? THEME.error : THEME.success}40`
                    }}
                  />
                </Box>
              )}

              {quotaInfo && (quotaInfo.used_today || 0) >= quotaInfo.daily_limit && quotaInfo.enforce_strict_stop && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  <strong>Daily Link Quota Reached ({quotaInfo.daily_limit} / {quotaInfo.daily_limit}):</strong> Your dealership has processed its maximum allowed videos for today. Submissions are temporarily paused. Contact your administrator or Master Admin for quota expansion.
                </Alert>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: `1px solid ${THEME.errorLight}`,
                    backgroundColor: THEME.errorLight
                  }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={resetForm}
                      sx={{ fontWeight: 600 }}
                    >
                      Reset
                    </Button>
                  }
                >
                  <Typography variant="body2" fontWeight="600">
                    {error}
                  </Typography>
                </Alert>
              )}

              {/* ─── Video Submission Form (Top) ─── */}
              <form onSubmit={submit}>
                <Grid container spacing={2.5} sx={{ mb: currentTask || resultData ? 4 : 0 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VideoCameraBack sx={{ color: THEME.textSecondary, fontSize: 20 }} />
                          <span>QualityLens Video URL</span>
                        </Box>
                      }
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      required
                      disabled={localLoading || (currentTask && ['pending', 'processing'].includes(currentTask.status))}
                      placeholder="e.g. https://southasia.citnow.com/..."
                      helperText="Enter the full URL of your video for analysis"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: THEME.surface,
                          transition: 'all 0.2s ease',
                          '& fieldset': { borderColor: THEME.border },
                          '&:hover fieldset': { borderColor: THEME.primaryLight },
                          '&.Mui-focused fieldset': {
                            borderColor: THEME.primary,
                            borderWidth: '2px'
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(28, 63, 170, 0.08)'
                          }
                        }
                      }}
                    />
                  </Grid>

                  {role === 'super_admin' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business sx={{ color: THEME.primary, fontSize: 20 }} />
                            <span>Target Dealership (Super Admin Multi-Dealer Optimization)</span>
                          </Box>
                        }
                        value={selectedDealer}
                        onChange={(e) => setSelectedDealer(e.target.value)}
                        disabled={localLoading || (currentTask && ['pending', 'processing'].includes(currentTask.status))}
                        helperText="Assign this video to a specific dealership, or leave as Auto-Detect to read dealership from CitNow metadata"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: THEME.surface,
                            transition: 'all 0.2s ease',
                            '& fieldset': { borderColor: THEME.border },
                            '&:hover fieldset': { borderColor: THEME.primaryLight },
                            '&.Mui-focused fieldset': {
                              borderColor: THEME.primary,
                              borderWidth: '2px'
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#fff',
                              boxShadow: '0 4px 12px rgba(28, 63, 170, 0.08)'
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Auto-Detect Dealership from Video URL (Recommended)</em>
                        </MenuItem>
                        {dealersList.map((d) => (
                          <MenuItem key={d} value={d}>
                            {d}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Language sx={{ color: THEME.textSecondary, fontSize: 20 }} />
                          <span>Spoken Language</span>
                        </Box>
                      }
                      value={lang}
                      onChange={(e) => setLang(e.target.value)}
                      disabled={localLoading || (currentTask && ['pending', 'processing'].includes(currentTask.status))}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: THEME.surface,
                          transition: 'all 0.2s ease',
                          '& fieldset': { borderColor: THEME.border },
                          '&:hover fieldset': { borderColor: THEME.primaryLight },
                          '&.Mui-focused fieldset': {
                            borderColor: THEME.primary,
                            borderWidth: '2px'
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(28, 63, 170, 0.08)'
                          }
                        }
                      }}
                    >
                      {LANGS.map(l => (
                        <MenuItem key={l.code} value={l.code}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="body1">{l.icon}</Typography>
                            <Typography>{l.name}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Translate sx={{ color: THEME.textSecondary, fontSize: 20 }} />
                          <span>Target Language</span>
                        </Box>
                      }
                      value={target}
                      onChange={(e) => setTarget(e.target.value)}
                      disabled={localLoading || (currentTask && ['pending', 'processing'].includes(currentTask.status))}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: THEME.surface,
                          transition: 'all 0.2s ease',
                          '& fieldset': { borderColor: THEME.border },
                          '&:hover fieldset': { borderColor: THEME.primaryLight },
                          '&.Mui-focused fieldset': {
                            borderColor: THEME.primary,
                            borderWidth: '2px'
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(28, 63, 170, 0.08)'
                          }
                        }
                      }}
                    >
                      {LANGS.filter(l => l.code !== 'auto').map(l => (
                        <MenuItem key={l.code} value={l.code}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="body1">{l.icon}</Typography>
                            <Typography>{l.name}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={localLoading || (currentTask && ['pending', 'processing'].includes(currentTask.status)) || (quotaInfo && (quotaInfo.used_today || 0) >= quotaInfo.daily_limit && quotaInfo.enforce_strict_stop)}
                        startIcon={<PlayArrow />}
                        sx={{
                          background: THEME.gradientPrimary,
                          borderRadius: 3,
                          px: 5,
                          py: 1.5,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: '1rem',
                          boxShadow: '0 8px 20px -6px rgba(28, 105, 212, 0.5)',
                          '&:hover': {
                            boxShadow: '0 12px 25px -6px rgba(28, 105, 212, 0.6)',
                            transform: 'translateY(-2px)'
                          },
                          '&:disabled': {
                            background: THEME.textTertiary,
                            transform: 'none',
                            boxShadow: 'none'
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          minWidth: 160
                        }}
                      >
                        {localLoading ? 'Starting Analysis...' : 'Start Analysis'}
                      </Button>

                      {(resultData || (currentTask && (currentTask.status === 'completed' || currentTask.status === 'failed'))) && (
                        <Button
                          variant="outlined"
                          onClick={resetForm}
                          startIcon={<Add />}
                          sx={{
                            borderRadius: 3,
                            px: 3.5,
                            py: 1.4,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            borderColor: THEME.primary,
                            color: THEME.primary,
                            '&:hover': {
                              backgroundColor: THEME.primaryUltraLight,
                              borderColor: THEME.primaryDark
                            }
                          }}
                        >
                          New Analysis
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>

              {/* ─── Task Status & Progress ─── */}
              {currentTask && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    backgroundColor: STATUS_CONFIG[currentTask.status]?.bgColor,
                    border: `1px solid ${THEME.borderLight}`
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: THEME.background,
                        border: `1px solid ${THEME.border}`
                      }}>
                        {STATUS_CONFIG[currentTask.status]?.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="600" sx={{ color: STATUS_CONFIG[currentTask.status]?.textColor }}>
                          Analysis {STATUS_CONFIG[currentTask.status]?.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: THEME.textSecondary, mt: 0.5 }}>
                          Task ID: {currentTask.task_id}
                        </Typography>
                      </Box>
                    </Box>

                    <Chip
                      label={STATUS_CONFIG[currentTask.status]?.label}
                      sx={{
                        backgroundColor: STATUS_CONFIG[currentTask.status]?.bgColor,
                        color: STATUS_CONFIG[currentTask.status]?.textColor,
                        fontWeight: 600,
                        border: `1px solid ${STATUS_CONFIG[currentTask.status]?.textColor}20`
                      }}
                    />
                  </Box>

                  {currentTask.status === 'processing' && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 5, 
                      mb: 2,
                      background: '#0a0a0a',
                      borderRadius: 4,
                      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                      border: '1px solid #222'
                    }}>
                      <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={130}
                          thickness={4}
                          sx={{ color: '#222' }}
                        />
                        <CircularProgress
                          variant="determinate"
                          value={progressPct}
                          size={130}
                          thickness={5}
                          sx={{
                            color: '#A3E635',
                            position: 'absolute',
                            left: 0,
                            '& .MuiCircularProgress-circle': {
                              strokeLinecap: 'round',
                              transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)',
                            },
                          }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            pointerEvents: 'none',
                          }}
                        >
                          <Typography
                            component="div"
                            sx={{ 
                              color: '#FFFFFF !important', 
                              fontWeight: 900,
                              fontSize: '2.5rem',
                              lineHeight: 1,
                              letterSpacing: '-0.5px',
                              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Roboto", sans-serif',
                              textShadow: '0 0 20px rgba(163, 230, 53, 0.5), 0 2px 10px rgba(0, 0, 0, 0.9)',
                              display: 'flex',
                              alignItems: 'baseline',
                              justifyContent: 'center',
                            }}
                          >
                            <span style={{ color: '#FFFFFF' }}>{Math.round(progressPct)}</span>
                            <Box
                              component="span"
                              sx={{
                                fontSize: '1.4rem',
                                ml: 0.5,
                                color: '#A3E635 !important',
                                fontWeight: 800,
                                textShadow: '0 0 10px rgba(163, 230, 53, 0.8)'
                              }}
                            >
                              %
                            </Box>
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ 
                          color: '#A3E635 !important', 
                          fontWeight: 800, 
                          letterSpacing: '2.5px', 
                          textTransform: 'uppercase',
                          fontSize: '0.8rem',
                          textShadow: '0 0 12px rgba(163, 230, 53, 0.6), 0 2px 8px rgba(0, 0, 0, 0.9)',
                          opacity: 1
                        }}
                      >
                        {currentTask.message || "ANALYZING VIDEO CONTENT"}
                      </Typography>
                      <Box sx={{ 
                        mt: 1, 
                        width: '40px', 
                        height: '2px', 
                        background: '#A3E635', 
                        borderRadius: 1,
                        animation: 'pulse 2s infinite'
                      }} />
                      <style>
                        {`
                          @keyframes pulse {
                            0% { opacity: 0.3; width: 20px; }
                            50% { opacity: 1; width: 60px; }
                            100% { opacity: 0.3; width: 20px; }
                          }
                        `}
                      </style>
                    </Box>
                  )}

                  {currentTask.status === 'completed' && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 5, 
                      mb: 2,
                      background: '#0a0a0a',
                      borderRadius: 4,
                      border: `1px solid ${THEME.success}40`,
                      boxShadow: `0 10px 30px ${THEME.success}15`
                    }}>
                      <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={130}
                          thickness={5}
                          sx={{
                            color: THEME.success,
                            '& .MuiCircularProgress-circle': {
                              strokeLinecap: 'round',
                            },
                          }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Check sx={{ color: THEME.success, fontSize: '4rem' }} />
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 800, textTransform: 'uppercase' }}>
                        Analysis Complete
                      </Typography>
                      <Typography variant="caption" sx={{ color: THEME.success, fontWeight: 600, mt: 1 }}>
                        READY FOR REVIEW
                      </Typography>
                    </Box>
                  )}

                  {currentTask.message && (
                    <Typography variant="body2" sx={{ color: THEME.textPrimary, mb: 1 }}>
                      {currentTask.message}
                    </Typography>
                  )}

                  {currentTask.status === 'completed' && currentTask.result_id && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      p: 2,
                      mt: 2,
                      borderRadius: 2,
                      backgroundColor: THEME.successLight,
                      border: `1px solid ${THEME.success}20`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Check sx={{ color: THEME.success }} />
                        <Typography variant="body2" fontWeight="600" sx={{ color: THEME.success }}>
                          Analysis completed successfully!
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          const elem = document.getElementById('inline-analysis-result');
                          if (elem) {
                            elem.scrollIntoView({ behavior: 'smooth' });
                          } else {
                            window.location.href = `/dealer/results?id=${currentTask.result_id}`;
                          }
                        }}
                        sx={{
                          bgcolor: THEME.success,
                          color: '#fff',
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: 2,
                          px: 2.5,
                          py: 0.8,
                          boxShadow: `0 2px 8px ${THEME.success}40`,
                          '&:hover': { bgcolor: '#047857' }
                        }}
                      >
                        View Analysis Results ↓
                      </Button>
                    </Box>
                  )}

                  {currentTask.status === 'failed' && currentTask.error_message && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                      mt: 2,
                      borderRadius: 2,
                      backgroundColor: THEME.errorLight,
                      border: `1px solid ${THEME.error}20`
                    }}>
                      <Error sx={{ color: THEME.error }} />
                      <Typography variant="body2" fontWeight="600" sx={{ color: THEME.error }}>
                        Error: {currentTask.error_message}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}

              {/* ─── Inline Result Display ─── */}
              {resultData && (() => {
                const formatDurationDisplay = (val) => {
                  if (!val && val !== 0) return null;
                  const num = typeof val === 'number' ? val : parseFloat(String(val).replace('s', ''));
                  if (isNaN(num) || num <= 0) return null;
                  const mins = Math.floor(num / 60);
                  const secs = Math.round(num % 60);
                  if (mins > 0) {
                    return `${mins}m ${secs < 10 ? '0' : ''}${secs}s (${num.toFixed(1)}s)`;
                  }
                  return `${num.toFixed(1)}s`;
                };

                const rawDuration = resultData.video_duration_formatted ||
                  resultData.video_duration ||
                  resultData.inspection_segment?.total_video_duration_formatted ||
                  resultData.inspection_segment?.total_video_duration ||
                  resultData.video_analysis?.detailed_analysis?.duration ||
                  resultData.duration ||
                  resultData.citnow_metadata?.duration ||
                  null;

                const videoLengthFormatted = typeof rawDuration === 'string' && rawDuration.includes('m')
                  ? rawDuration
                  : formatDurationDisplay(rawDuration);

                return (
                  <Box id="inline-analysis-result" sx={{ mt: 3, pt: 3, borderTop: `1.5px dashed ${THEME.border}` }}>
                    {/* Result Header with Badges */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
                      <Typography variant="h6" fontWeight="800" sx={{
                        color: THEME.textPrimary,
                        display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.2rem'
                      }}>
                        <Check sx={{ color: THEME.success, fontSize: 26 }} /> Analysis Result
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {videoLengthFormatted && (
                          <Chip
                            label={`⏱️ Video Length: ${videoLengthFormatted}`}
                            size="small"
                            sx={{
                              fontWeight: 800,
                              background: '#FEF3C7',
                              color: '#92400E',
                              border: '1px solid #F59E0B60',
                              borderRadius: 1.5,
                              fontSize: '0.78rem'
                            }}
                          />
                        )}
                        {resultData.overall_quality?.overall_label && (
                          <Chip
                            label={`Quality: ${resultData.overall_quality.overall_label}`}
                            size="small"
                            sx={{
                              fontWeight: 800,
                              background: THEME.primaryUltraLight,
                              color: THEME.primary,
                              border: `1px solid ${THEME.primary}30`,
                              borderRadius: 1.5,
                              fontSize: '0.78rem'
                            }}
                          />
                        )}
                        {resultData.citnow_metadata?.service_advisor && (
                          <Chip
                            label={`Advisor: ${resultData.citnow_metadata.service_advisor}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 700, borderRadius: 1.5, borderColor: THEME.border, color: THEME.textSecondary, fontSize: '0.78rem' }}
                          />
                        )}
                      </Box>
                    </Box>

                  {/* Full Cover Rating Score Cards */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 2.5,
                    width: '100%',
                    mb: 3
                  }}>
                    {[
                      {
                        label: 'Video Quality',
                        subtitle: 'Visual & Camera Framing',
                        value: resultData.video_analysis?.quality_score ?? resultData.video_quality_score ?? 0,
                        color: THEME.primary,
                        badgeColor: (resultData.video_analysis?.quality_score ?? resultData.video_quality_score ?? 0) >= 7 ? THEME.success : THEME.warning,
                        badgeBg: (resultData.video_analysis?.quality_score ?? resultData.video_quality_score ?? 0) >= 7 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                        badgeText: (resultData.video_analysis?.quality_score ?? resultData.video_quality_score ?? 0) >= 7 ? 'GOOD' : 'FAIR',
                        issues: resultData.video_analysis?.issues || [],
                        tips: [
                          ...(resultData.video_analysis?.issues || []).map(i => `Fix: ${i}`),
                          'Ensure good, bright lighting',
                          'Use a stable tripod or gimbal',
                          'Shoot in HD (1080p or higher)',
                          'Keep the camera lens clean and focused',
                        ]
                      },
                      {
                        label: 'Audio Quality',
                        subtitle: 'Service Bay Acoustics & Voice',
                        value: resultData.audio_analysis?.score ?? resultData.audio_quality_score ?? 0,
                        color: THEME.accent,
                        badgeColor: (resultData.audio_analysis?.score ?? resultData.audio_quality_score ?? 0) >= 7 ? THEME.success : THEME.primary,
                        badgeBg: (resultData.audio_analysis?.score ?? resultData.audio_quality_score ?? 0) >= 7 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(13, 161, 184, 0.12)',
                        badgeText: (resultData.audio_analysis?.score ?? resultData.audio_quality_score ?? 0) >= 7 ? 'GOOD' : 'FAIR',
                        issues: resultData.audio_analysis?.issues || [],
                        tips: [
                          ...(resultData.audio_analysis?.issues || []).map(i => `Fix: ${i}`),
                          'Use an external microphone',
                          'Record in a quiet environment',
                          'Speak clearly and at a consistent volume',
                          'Reduce wind and background noise',
                        ]
                      },
                      {
                        label: 'Overall Score',
                        subtitle: 'Technical Quality Index',
                        value: resultData.overall_quality?.overall_score ?? resultData.overall_quality_score ?? 0,
                        color: THEME.success,
                        badgeColor: (resultData.overall_quality?.overall_score ?? resultData.overall_quality_score ?? 0) >= 7 ? THEME.success : THEME.warning,
                        badgeBg: (resultData.overall_quality?.overall_score ?? resultData.overall_quality_score ?? 0) >= 7 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                        badgeText: (resultData.overall_quality?.overall_score ?? resultData.overall_quality_score ?? 0) >= 7 ? 'GOOD QUALITY' : 'FAIR QUALITY',
                        issues: [
                          ...(resultData.video_analysis?.issues || []),
                          ...(resultData.audio_analysis?.issues || [])
                        ],
                        tips: [
                          'Improve both video and audio quality',
                          'Ensure complete transcription with clear speech',
                          'Minimize all detected issues listed above',
                          'Maintain professional camera framing throughout',
                        ]
                      },
                    ].map(({ label, subtitle, value, color, badgeColor, badgeBg, badgeText, issues, tips }) => {
                      const tooltipContent = (
                        <Box sx={{ p: 0.5, maxWidth: 280 }}>
                          {issues.length > 0 && (
                            <>
                              <Typography variant="caption" fontWeight="700" sx={{ color: '#FFB400', display: 'block', mb: 0.5 }}>
                                ⚠️ Why this score:
                              </Typography>
                              {issues.slice(0, 3).map((issue, i) => (
                                <Typography key={i} variant="caption" sx={{ display: 'block', mb: 0.3, color: '#ffd580' }}>
                                  • {issue}
                                </Typography>
                              ))}
                              <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.15)', my: 1 }} />
                            </>
                          )}
                          <Typography variant="caption" fontWeight="700" sx={{ color: '#90EE90', display: 'block', mb: 0.5 }}>
                            🚀 To reach 10/10:
                          </Typography>
                          {tips.slice(0, 4).map((tip, i) => (
                            <Typography key={i} variant="caption" sx={{ display: 'block', mb: 0.3, color: '#d4edda' }}>
                              ✓ {tip}
                            </Typography>
                          ))}
                        </Box>
                      );
                      return (
                        <Paper
                          elevation={0}
                          key={label}
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            textAlign: 'center',
                            border: `1.5px solid ${THEME.border}`,
                            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 130,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                              borderColor: color
                            }
                          }}
                        >
                          <Tooltip
                            title={tooltipContent}
                            placement="top"
                            arrow
                            componentsProps={{ tooltip: { sx: { bgcolor: '#1a2a3a', maxWidth: 300, p: 1.5, borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' } }, arrow: { sx: { color: '#1a2a3a' } } }}
                          >
                            <HelpOutline
                              sx={{
                                position: 'absolute', top: 10, right: 10,
                                fontSize: 16, color: THEME.textTertiary,
                                cursor: 'help',
                                '&:hover': { color }
                              }}
                            />
                          </Tooltip>

                          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="h3" fontWeight="800" sx={{ color, letterSpacing: '-0.5px', fontSize: { xs: '2.1rem', md: '2.5rem' } }}>
                              {Number(value).toFixed(1)}
                            </Typography>
                            <Typography variant="body2" fontWeight="700" sx={{ color: THEME.textTertiary }}>
                              / 10
                            </Typography>
                          </Box>

                          <Typography variant="subtitle2" fontWeight="800" sx={{ color: THEME.textPrimary, mb: 0.75, fontSize: '0.96rem' }}>
                            {label}
                          </Typography>

                          <Chip
                            label={`${badgeText} • ${Number(value).toFixed(1)}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontWeight: 800,
                              fontSize: '0.68rem',
                              letterSpacing: '0.5px',
                              bgcolor: badgeBg,
                              color: badgeColor,
                              borderRadius: 1.5
                            }}
                          />
                        </Paper>
                      );
                    })}
                  </Box>

                  {/* Vehicle & Case Details */}
                  {(() => {
                    const m = resultData.citnow_metadata || {};
                    const vehicleName = m.vehicle && m.vehicle !== m.registration ? m.vehicle : (m.brand ? `${m.brand} Vehicle` : null);
                    const completedDt = resultData.created_at || resultData.processing_timestamp || m.date || m.created_at;
                    const details = [
                      ...(videoLengthFormatted ? [{ label: '⏱️ Video Length / Duration', value: videoLengthFormatted }] : []),
                      ...(vehicleName ? [{ label: '🚗 Vehicle', value: vehicleName }] : []),
                      { label: '🔖 Registration', value: m.registration || m.reg_no || m.vehicle || '—' },
                      { label: '🔢 VIN', value: m.vin || '—' },
                      { label: '🏢 Dealership', value: m.dealership || '—' },
                      { label: '👤 Service Advisor', value: m.service_advisor || '—' },
                      { label: '📧 Email', value: m.email || '—' },
                      { label: '📞 Phone', value: m.phone || '—' },
                      ...(completedDt ? [{ label: '📅 Analyzed', value: formatDateTime(completedDt) }] : []),
                    ].filter(d => d.value && d.value !== '—');

                    return (
                      <>
                        {/* Real Rating / Satisfaction (If available) */}
                        {(m.star_rating !== undefined || m.customer_feedback) && (
                          <Paper elevation={0} sx={{
                            p: 2.5, mb: 2, borderRadius: 2,
                            border: `1px solid ${THEME.success}30`,
                            background: `${THEME.success}05`
                          }}>
                            <Typography variant="subtitle2" fontWeight="700" sx={{ color: THEME.success, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                              ⭐ Customer Satisfaction (Real Rating)
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: m.customer_feedback ? 2 : 0 }}>
                              {m.star_rating !== undefined && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <Typography key={star} sx={{ 
                                      color: star <= m.star_rating ? '#FFB400' : '#E0E0E0',
                                      fontSize: '24px',
                                      lineHeight: 1
                                    }}>
                                      ★
                                    </Typography>
                                  ))}
                                  <Typography sx={{ ml: 1, fontWeight: 700, color: THEME.textPrimary }}>
                                    {m.star_rating}/5
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            {m.customer_feedback && (
                              <Box sx={{ p: 1.5, background: '#FFFFFF', borderRadius: 1.5, borderLeft: `3px solid ${THEME.success}` }}>
                                <Typography variant="body2" italic sx={{ color: THEME.textSecondary }}>
                                  "{m.customer_feedback}"
                                </Typography>
                              </Box>
                            )}
                          </Paper>
                        )}

                        {details.length > 0 && (
                          <Paper elevation={0} sx={{
                            p: 2.5, mb: 2.5, borderRadius: 2.5,
                            border: `1px solid ${THEME.border}`,
                            background: '#FFFFFF',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                          }}>
                            <Typography variant="subtitle2" fontWeight="800" sx={{ color: THEME.textPrimary, mb: 2, fontSize: '0.96rem' }}>
                              🚘 Vehicle & Case Details
                            </Typography>
                            <Grid container spacing={2}>
                              {details.map(({ label, value }) => (
                                <Grid item xs={12} sm={6} md={4} key={label}>
                                  <Box sx={{ p: 1.25, bgcolor: '#F8FAFC', borderRadius: 2, border: `1px solid ${THEME.borderLight}` }}>
                                    <Typography variant="caption" sx={{ color: THEME.textSecondary, fontWeight: 700, display: 'block', mb: 0.25, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                      {label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700, wordBreak: 'break-word', fontSize: '0.92rem' }}>
                                      {value}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>
                        )}
                      </>
                    );
                  })()}

                  {/* Executive Inspection Summary */}
                  {(() => {
                    const rawSummary = resultData.summarization?.summary || '';
                    const summaryData = formatExecutiveSummary(rawSummary, resultData);
                    const {
                      overallScore, overallLabel,
                      videoScore, videoLabel,
                      audioScore, audioLabel,
                      executiveText, visualText, acousticText,
                      spokenFindings, recommendation
                    } = summaryData;

                    return (
                      <Paper elevation={0} sx={{
                        p: 3, mb: 3, borderRadius: 3,
                        border: `1px solid ${THEME.border}`,
                        background: '#FFFFFF',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.03)'
                      }}>
                        {/* Summary Header: Cohesive, Balanced Placement (NOT at far right edge) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" fontWeight="800" sx={{ color: THEME.textPrimary, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.05rem' }}>
                            💡 Executive Inspection Summary
                          </Typography>
                          <Chip
                            label={`${overallLabel} • ${overallScore} / 10`}
                            size="small"
                            sx={{
                              fontWeight: 800,
                              fontSize: '0.78rem',
                              height: 26,
                              px: 0.5,
                              bgcolor: Number(overallScore) >= 7.0 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                              color: Number(overallScore) >= 7.0 ? THEME.success : THEME.warning,
                              border: `1px solid ${Number(overallScore) >= 7.0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                            }}
                          />
                        </Box>

                        {/* Executive Assessment: High Contrast, Crisp Readability */}
                        <Box sx={{ mb: 2.5, p: 2.5, bgcolor: '#F8FAFC', borderRadius: 2.5, borderLeft: `5px solid ${THEME.primary}`, border: `1px solid ${THEME.border}` }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: THEME.primaryDark, textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', mb: 1, fontSize: '0.8rem' }}>
                            Executive Assessment
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#0F172A', lineHeight: 1.75, fontSize: '0.96rem', fontWeight: 500 }}>
                            {executiveText}
                          </Typography>
                        </Box>

                        {/* Visual & Acoustic Quality Breakdown Grid: Full Cover */}
                        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
                          {/* Visual Performance */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2.5, bgcolor: '#FFFFFF', borderRadius: 2.5, height: '100%', border: `1.5px solid ${THEME.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: THEME.textPrimary, fontSize: '0.94rem' }}>
                                  🎥 Visual & Camera Quality
                                </Typography>
                                <Chip
                                  label={`${videoScore} / 10 • ${videoLabel}`}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.72rem',
                                    fontWeight: 800,
                                    bgcolor: Number(videoScore) >= 7.0 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                                    color: Number(videoScore) >= 7.0 ? THEME.success : THEME.warning
                                  }}
                                />
                              </Box>
                              <Typography variant="body2" sx={{ color: '#1E293B', lineHeight: 1.7, fontSize: '0.93rem' }}>
                                {visualText}
                              </Typography>
                            </Box>
                          </Grid>

                          {/* Acoustic Quality */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2.5, bgcolor: '#FFFFFF', borderRadius: 2.5, height: '100%', border: `1.5px solid ${THEME.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: THEME.textPrimary, fontSize: '0.94rem' }}>
                                  🎙️ Acoustic Profile & Audio
                                </Typography>
                                <Chip
                                  label={`${audioScore} / 10 • ${audioLabel}`}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.72rem',
                                    fontWeight: 800,
                                    bgcolor: 'rgba(13, 161, 184, 0.12)',
                                    color: THEME.primary
                                  }}
                                />
                              </Box>
                              <Typography variant="body2" sx={{ color: '#1E293B', lineHeight: 1.7, fontSize: '0.93rem' }}>
                                {acousticText}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Spoken Findings if Speech Was Present */}
                        {spokenFindings && (
                          <Box sx={{ mb: 2.5, p: 2.5, bgcolor: 'rgba(124, 58, 237, 0.04)', borderRadius: 2.5, border: '1.5px solid rgba(124, 58, 237, 0.2)' }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', mb: 1, fontSize: '0.8rem' }}>
                              🎙️ Spoken Technician Observations
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#0F172A', lineHeight: 1.7, fontSize: '0.94rem' }}>
                              {spokenFindings}
                            </Typography>
                          </Box>
                        )}

                        {/* Advisor Best Practice & Presentation Takeaway */}
                        <Box sx={{
                          p: 2.25,
                          bgcolor: Number(overallScore) >= 7.0 ? '#F0FDF4' : '#FFFBE8',
                          borderRadius: 2.5,
                          border: `1.5px dashed ${Number(overallScore) >= 7.0 ? 'rgba(16, 185, 129, 0.4)' : 'rgba(245, 158, 11, 0.4)'}`,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5
                        }}>
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>✨</Typography>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: Number(overallScore) >= 7.0 ? '#065F46' : '#92400E', display: 'block', mb: 0.5, fontSize: '0.92rem' }}>
                              Advisor Presentation Best Practice
                            </Typography>
                            <Typography variant="body2" sx={{ color: Number(overallScore) >= 7.0 ? '#065F46' : '#92400E', lineHeight: 1.65, fontSize: '0.93rem', fontWeight: 500 }}>
                              {recommendation}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })()}

                  {/* Transcription (Original Speech) */}
                  {(() => {
                    const GARBAGE = ['the', 'a', ',', '.', '!', '?', '...', ',.']
                    const GARBAGE_PHRASES = [
                      'no clear speech detected', 'transcription failed', 'translation failed', 
                      'summarization failed', 'no meaningful summary', 'let me get out of here',
                      'it\'s not made because i hate it', 'thank you for watching', 'subtitles by'
                    ]
                    const rawText = typeof resultData.transcription === 'string'
                      ? resultData.transcription
                      : (resultData.transcription?.text || resultData.audio_analysis?.transcription || resultData.audio_analysis?.transcription_text || '')
                    // Check if repeating hallucination pattern
                    const isRepeating = /(..+?)\1{4,}/.test(rawText)
                    const isGarbage = isRepeating || GARBAGE.includes(rawText.trim().toLowerCase()) || GARBAGE_PHRASES.some(p => rawText.trim().toLowerCase().includes(p))
                    const hasSpeech = rawText.trim().length >= 5 && !isGarbage
                    const spokenLang = (typeof resultData.transcription === 'object' && resultData.transcription?.language) || resultData.transcription_language || 'Auto'

                    return (
                      <Paper elevation={0} sx={{
                        p: 2.5, mb: 2, borderRadius: 2,
                        border: `1px solid ${THEME.border}`,
                        background: THEME.surface,
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight="700" sx={{ color: THEME.textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}>
                            🎙️ Transcription (Original Spoken Audio)
                          </Typography>
                          <Chip
                            label={`Spoken: ${spokenLang.toUpperCase()}`}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              height: 22,
                              background: THEME.primaryUltraLight,
                              color: THEME.primary
                            }}
                          />
                        </Box>
                        {hasSpeech ? (
                          <Typography variant="body2" sx={{ color: THEME.textPrimary, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {rawText}
                          </Typography>
                        ) : (
                          <Box sx={{ p: 2, background: '#F8FAFC', borderRadius: 2, border: `1px dashed ${THEME.border}` }}>
                            <Typography variant="body2" sx={{ color: THEME.textSecondary, fontWeight: 600 }}>
                              🔇 Silent Walkaround Inspection
                            </Typography>
                            <Typography variant="caption" sx={{ color: THEME.textTertiary, mt: 0.5, display: 'block', lineHeight: 1.5 }}>
                              No technician speech or audio narrative was identified in this recording (purely visual walkaround). CitNow videos with verbal speech will transcribe automatically here.
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    );
                  })()}

                  {/* Translation */}
                  {(() => {
                    const GARBAGE = ['the', 'a', ',', '.', '!', '?', '...', ',.'];
                    const GARBAGE_PHRASES = [
                      'no clear speech detected', 'transcription failed', 'translation failed', 
                      'summarization failed', 'no meaningful summary', 'let me get out of here',
                      'it\'s not made because i hate it', 'thank you for watching', 'subtitles by'
                    ];

                    const rawTranscription = typeof resultData.transcription === 'string'
                      ? resultData.transcription
                      : (resultData.transcription?.text || resultData.audio_analysis?.transcription || resultData.audio_analysis?.transcription_text || '');
                    
                    const isRepeatingTranscription = /(..+?)\1{4,}/.test(rawTranscription);
                    const hasValidSpeech = rawTranscription.trim().length >= 5 && !isRepeatingTranscription && !GARBAGE.includes(rawTranscription.trim().toLowerCase()) && !GARBAGE_PHRASES.some(p => rawTranscription.trim().toLowerCase().includes(p));

                    let rawTranslation = typeof resultData.translation === 'string'
                      ? resultData.translation
                      : (resultData.translation?.translated_text || resultData.translation?.text || resultData.audio_analysis?.translation || resultData.audio_analysis?.translated_text || resultData.native_english_transcription || '');
                    
                    const spokenLang = ((typeof resultData.transcription === 'object' && resultData.transcription?.language) || resultData.transcription_language || '').toLowerCase();
                    const isEnglishSpoken = spokenLang.includes('en') || spokenLang === '';

                    // If translation is empty or not provided, but valid English speech was spoken, use transcription as translation
                    if ((!rawTranslation || rawTranslation.trim().length < 3) && isEnglishSpoken && hasValidSpeech) {
                      rawTranslation = rawTranscription;
                    }

                    const isRepeating = /(..+?)\1{4,}/.test(rawTranslation);
                    const isVisualSummary = rawTranslation.includes('documents a walkaround') || rawTranslation.includes('visual inspection walkaround') || rawTranslation.includes('The lighting is clean');
                    const isGarbage = isRepeating || GARBAGE.includes(rawTranslation.trim().toLowerCase()) || GARBAGE_PHRASES.some(p => rawTranslation.trim().toLowerCase().includes(p)) || isVisualSummary;
                    const hasTranslation = rawTranslation.trim().length >= 3 && !isGarbage;
                    const targetLang = (typeof resultData.translation === 'object' && resultData.translation?.target_language) || resultData.target_language_used || 'EN';

                    return (
                      <Paper elevation={0} sx={{
                        p: 2.5, mb: 2, borderRadius: 2,
                        border: `1px solid ${THEME.border}`,
                        background: THEME.surface,
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight="700" sx={{ color: THEME.textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}>
                            🌐 Translation (Spoken Words Translated to English)
                          </Typography>
                          <Chip
                            label={`Target: ${targetLang.toUpperCase()}`}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              height: 22,
                              background: THEME.accentUltraLight,
                              color: THEME.accent
                            }}
                          />
                        </Box>
                        {hasTranslation ? (
                          <Typography variant="body2" sx={{ color: THEME.textPrimary, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {rawTranslation}
                          </Typography>
                        ) : (
                          <Box sx={{ p: 2, background: '#F8FAFC', borderRadius: 2, border: `1px dashed ${THEME.border}` }}>
                            <Typography variant="body2" sx={{ color: THEME.textSecondary, fontWeight: 600 }}>
                              🌐 No Speech to Translate
                            </Typography>
                            <Typography variant="caption" sx={{ color: THEME.textTertiary, mt: 0.5, display: 'block', lineHeight: 1.5 }}>
                              Since this is a visual inspection walkaround without spoken audio, translation is not required.
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    );
                  })()}
                </Box>
              );
            })()}
              {/* Help section */}
              {!currentTask && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    mt: 5,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${THEME.surface} 0%, rgba(248, 250, 252, 0.4) 100%)`,
                    border: '1px solid',
                    borderColor: 'rgba(28, 105, 212, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.01)'
                  }}
                >
                  {/* Decorative glow inside help box */}
                  <Box sx={{
                    position: 'absolute', top: -30, right: -30, width: 100, height: 100,
                    background: 'radial-gradient(circle, rgba(28,105,212,0.1) 0%, transparent 70%)',
                    borderRadius: '50%'
                  }} />
                  <Typography variant="subtitle1" fontWeight="700" sx={{ color: THEME.primary, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>💡</span> How it works
                  </Typography>
                  <Box sx={{
                    display: 'flex', flexDirection: 'column', gap: 1.5,
                    '& p': { color: THEME.textSecondary, display: 'flex', alignItems: 'center', gap: 1.5, m: 0, fontSize: '0.95rem' },
                    '& span.bullet': { color: THEME.primaryLight, fontSize: '1.5rem', lineHeight: 0.5 }
                  }}>
                    <p><span className="bullet">•</span> Analysis runs seamlessly in the background — feel free to continue using the dashboard.</p>
                    <p><span className="bullet">•</span> Real-time progress tracking with automatic status updates.</p>
                    <p><span className="bullet">•</span> Comprehensive video, audio, and transcription intelligence.</p>
                    <p><span className="bullet">•</span> Results are beautifully formatted and available immediately upon completion.</p>
                  </Box>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container >
  );
}